import {
  ToggleButtonGroup as MuiToggleButtonGroup,
  styled,
  useThemeProps,
  ToggleButtonGroupProps as MuiToggleButtonGroupProps,
} from '@mui/material';
import * as React from 'react';

export interface ToggleButtonGroupProps
  extends Omit<MuiToggleButtonGroupProps, 'orientation'> {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  color?: MuiToggleButtonGroupProps['color'];
  /**
   * If 'true', dividers are shown between each button in the group.
   * @default true
   */
  showDividers?: boolean;
}

const StyledToggleButtonGroup = styled(MuiToggleButtonGroup, {
  shouldForwardProp: (prop) => prop !== 'showDividers',
  // eslint-disable-next-line deprecation/deprecation
})<ToggleButtonGroupProps>(({ showDividers }) => ({
  ...(showDividers === false && {
    '& > *:not(.MuiToggleButtonGroup-lastButton)': {
      borderRight: 'none',
    },
    '& > *:not(.MuiToggleButtonGroup-firstButton)': {
      borderLeft: 'none',
    },
  }),
}));

/**
 * ToggleButtonGroup groups related toggle buttons together.
 *
 * Demos:
 *
 * - [Toggle Button — Group](https://unify.workiva.org/components/toggle-button?tab=code#toggle-button-group)
 *
 * API:
 *
 * - [ToggleButtonGroup API](https://unify.workiva.org/components/toggle-button?tab=props#togglebuttongroup)
 */
export const ToggleButtonGroup = React.forwardRef<
  HTMLButtonElement,
  ToggleButtonGroupProps
>(function ToggleButtonGroup(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'MuiToggleButtonGroup' });
  const { children, showDividers = true, ...rest } = props;

  return (
    <StyledToggleButtonGroup ref={ref} showDividers={showDividers} {...rest}>
      {children}
    </StyledToggleButtonGroup>
  );
});
export default ToggleButtonGroup;
